import { SelectorUtil } from '../../utils/selector-util';
import { DomUtils } from '../../utils/dom-utils';
import { BaseBuilderEntity } from '../base-entity.model';
import { HiddenFieldRenderer } from '../../renderers/fields/hidden-field-renderer';
import { HiddenFieldJsonData } from '../element-types';

export const validHiddenTypes = ['hidden'] as const;
export type HiddenType = typeof validHiddenTypes[number];

export type HiddenFieldDataType = 'date' | 'number' | 'text';

export enum HiddenFieldTags {
    SubmissionDate = '*|submission_date|*'
}

export interface IHiddenFieldBlock {
    readonly id: string;
    readonly htmlElement: HTMLElement;
    readonly elements: readonly HTMLElement[];
    readonly isEmpty: boolean;
    addElement(data?: Partial<HiddenFieldJsonData>): HTMLElement;
    getElement(elementId: string): HTMLElement | null;
    removeElement(elementId: string): void;
    initElements(): void;
    toJsonData(): HiddenFieldJsonData[];
}

export class HiddenFieldBlock extends BaseBuilderEntity implements IHiddenFieldBlock {
    public readonly elementsMap = new Map<string, HTMLElement>();
    private readonly renderer = new HiddenFieldRenderer();

    public get elements(): readonly HTMLElement[] {
        return Array.from(this.elementsMap.values());
    }

    public get isEmpty(): boolean {
        return this.htmlElement.childElementCount === 0;
    }

    public getElement(elementId: string): HTMLElement | null {
        return this.elementsMap.get(elementId) ?? null;
    }

    public addElement(data?: Partial<HiddenFieldJsonData>): HTMLElement {
        const id = data?.id || DomUtils.generateUniqueElementId([]);
        const fieldData: HiddenFieldJsonData = {
            id,
            type: 'hidden',
            value: data?.value ?? '',
            dataType: data?.dataType ?? 'text',
            label: data?.label ?? '',
            crmFieldData: data?.crmFieldData ?? null
        };

        const fieldHtml = this.renderer.render(fieldData);
        this.htmlElement.append(fieldHtml);
        this.elementsMap.set(fieldHtml.id, fieldHtml);
        return fieldHtml;
    }

    public removeElement(elementId: string): void {
        const element = this.elementsMap.get(elementId);
        if (!element) {
            console.warn('HiddenFieldBlock removeElement: element not found', elementId);
            return;
        }
        element.remove();
        this.elementsMap.delete(elementId);
    }

    public initElements(): void {
        this.elementsMap.clear();

        const elementsNodeList = this.htmlElement.querySelectorAll<HTMLElement>(SelectorUtil.builderElement.base);

        for (let i = 0; i < elementsNodeList.length; i++) {
            const elementNode = elementsNodeList.item(i);

            if (!elementNode.id) {
                console.warn('HiddenFieldBlock element', elementNode, 'not have id');
                continue;
            }

            if (elementNode.hasAttribute(SelectorUtil.elementAttr)) {
                const elementType = elementNode.getAttribute(SelectorUtil.elementAttr);

                if (validHiddenTypes.includes(elementType as HiddenType)) {
                    this.elementsMap.set(elementNode.id, elementNode);
                } else {
                    console.warn(`HiddenField with type "${elementType}" not found`);
                }
            }
        }
    }

    public toJsonData(): HiddenFieldJsonData[] {
        return this.elements.map(element => {
            const jsonAttr = element.getAttribute('data-element-json');
            if (jsonAttr) {
                return JSON.parse(jsonAttr) as HiddenFieldJsonData;
            }

            console.warn('Hidden field missing data-element-json:', element.id);
            return {
                id: element.id,
                type: 'hidden' as const,
                value: '',
                dataType: 'text',
                label: '',
                crmFieldData: null
            };
        });
    }
}
